// frontend/src/pages/StockAuditMobilePage.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Html5Qrcode } from 'html5-qrcode';
import Modal from '../components/Modal';
import { printLaserSheet } from '../utils/qzTrayService';
import './StockAuditMobilePage.css';

const QUICK_NOTE_CHIPS = [
  'Remnant waste',
  'Damaged end',
  'Mislabeled bolt',
  'Cutting allowance',
  'Recounted OK',
];

export function parseRackLocation(locStr) {
  if (!locStr || typeof locStr !== 'string') {
    return { line: '', rack: '', shelf: '' };
  }
  const clean = locStr.trim().replace(/^LOC:\s*/i, '');
  const parts = clean.split(/[-/,\s]+/).filter(Boolean);
  if (parts.length === 0) return { line: '', rack: '', shelf: '' };
  if (parts.length === 1) return { line: parts[0].toUpperCase(), rack: '', shelf: '' };
  if (parts.length === 2) return { line: parts[0].toUpperCase(), rack: parts[1].toUpperCase(), shelf: '' };
  return {
    line: parts[0].toUpperCase(),
    rack: parts[1].toUpperCase(),
    shelf: parts[2].toUpperCase(),
  };
}

export function composeRackLocation(line, rack, shelf) {
  const l = (line || '').trim().toUpperCase();
  const r = (rack || '').trim().toUpperCase();
  const s = (shelf || '').trim().toUpperCase();
  const parts = [];
  if (l) parts.push(l);
  if (r) parts.push(r);
  if (s) parts.push(s);
  return parts.join('-');
}

// Web Audio API Sound generator
function playAudioFeedback(type = 'success') {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'success') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } else if (type === 'warning') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(350, ctx.currentTime);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch (e) {
    // Ignore audio autoplay restrictions
  }
}

function triggerHaptic(type = 'success') {
  try {
    if (navigator.vibrate) {
      if (type === 'success') navigator.vibrate(80);
      else if (type === 'warning') navigator.vibrate([100, 50, 100]);
    }
  } catch (e) {
    // Ignore haptic errors
  }
}

function StockAuditMobilePage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : '';

  // Batches
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(() => localStorage.getItem('mobile_audit_batch_id') || '');
  const [activeBatch, setActiveBatch] = useState(null);

  // Rack Filter & SKUs
  const [activeRack, setActiveRack] = useState('ALL');
  const [availableRacks, setAvailableRacks] = useState(['ALL']);
  const [rackSkus, setRackSkus] = useState([]);
  const [showRackListModal, setShowRackListModal] = useState(false);

  // Scanner State
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const barcodeInputRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Active SKU Audit Data
  const [currentSkuData, setCurrentSkuData] = useState(null);
  const [isLoadingSku, setIsLoadingSku] = useState(false);
  const [pieceInputs, setPieceInputs] = useState([]);
  const [lastUsedLine, setLastUsedLine] = useState(() => localStorage.getItem('audit_last_line') || '');
  const [lastUsedRack, setLastUsedRack] = useState(() => localStorage.getItem('audit_last_rack') || '');
  const [newPieceDraft, setNewPieceDraft] = useState({
    pieceId: '',
    length: '',
    rackLine: localStorage.getItem('audit_last_line') || '',
    rackNumber: localStorage.getItem('audit_last_rack') || '',
    shelfNumber: '',
  });
  const [auditNote, setAuditNote] = useState('');
  const [countStartedAt, setCountStartedAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState(null);
  const [savedSummaryData, setSavedSummaryData] = useState(null);
  const [savingPieceId, setSavingPieceId] = useState(null);
  const [pieceAuditStatusMap, setPieceAuditStatusMap] = useState({});
  const [selectedPieceIds, setSelectedPieceIds] = useState(new Set());
  const [reprintingPieceId, setReprintingPieceId] = useState(null);
  const [isReprintingAll, setIsReprintingAll] = useState(false);
  const [reprintStatusMap, setReprintStatusMap] = useState({});

  const tokenHeader = useMemo(() => {
    return async () => {
      if (!currentUser) return {};
      const token = await currentUser.getIdToken();
      return { Authorization: `Bearer ${token}` };
    };
  }, [currentUser]);

  // Load Batches
  const fetchBatches = useCallback(async () => {
    if (!currentUser) return;
    try {
      const headers = await tokenHeader();
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches`, { headers });
      const data = await res.json();
      if (!res.ok) return;

      const list = Array.isArray(data.batches) ? data.batches : [];
      setBatches(list);

      if (list.length > 0) {
        if (!selectedBatchId || !list.some((b) => (b.batchId || b.id) === selectedBatchId)) {
          const inProgress = list.find((b) => b.status === 'in_progress') || list[0];
          const newId = inProgress.batchId || inProgress.id;
          setSelectedBatchId(newId);
          localStorage.setItem('mobile_audit_batch_id', newId);
        }
      }
    } catch (e) {
      console.warn('Failed to load batches:', e);
    }
  }, [apiBase, currentUser, selectedBatchId, tokenHeader]);

  // Load Batch metadata & Racks
  const fetchBatchDetails = useCallback(async () => {
    if (!selectedBatchId || !currentUser) return;
    try {
      const headers = await tokenHeader();
      const bRes = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}`, { headers });
      const bData = await bRes.json();
      if (bRes.ok && bData.batch) setActiveBatch(bData.batch);

      // Fetch all SKUs in this batch to extract racks
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/skus?limit=200`, { headers });
      const data = await res.json();
      if (res.ok && Array.isArray(data.skus)) {
        setRackSkus(data.skus);
        const rackSet = new Set(['ALL']);
        data.skus.forEach((s) => {
          if (Array.isArray(s.locations)) {
            s.locations.forEach((loc) => {
              if (loc) rackSet.add(loc.toUpperCase().trim());
            });
          }
        });
        setAvailableRacks(Array.from(rackSet));
      }
    } catch (e) {
      console.warn('Failed to fetch batch details:', e);
    }
  }, [apiBase, selectedBatchId, currentUser, tokenHeader]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if (selectedBatchId) {
      fetchBatchDetails();
      localStorage.setItem('mobile_audit_batch_id', selectedBatchId);
    }
  }, [selectedBatchId, fetchBatchDetails]);

  // Auto-focus barcode input
  useEffect(() => {
    if (!isCameraActive && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [isCameraActive, currentSkuData]);

  // Fetch SKU Pieces
  const loadSkuForAudit = useCallback(async (skuToLoad) => {
    const cleanSku = String(skuToLoad || '').trim().toUpperCase();
    if (!cleanSku || !selectedBatchId) return;

    setIsLoadingSku(true);
    setSavedSummaryData(null);
    setReprintStatusMap({});
    setPieceAuditStatusMap({});
    setSavingPieceId(null);
    setConcurrencyConflict(null);
    setAuditNote('');

    try {
      const headers = await tokenHeader();
      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(cleanSku)}`, { headers });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'SKU not found.');

      playAudioFeedback('success');
      triggerHaptic('success');

      setCurrentSkuData(data);
      setCountStartedAt(data.snapshotTimestamp || Date.now());

      const pieces = Array.isArray(data.pieces) ? data.pieces : [];
      let latestLine = lastUsedLine;
      let latestRack = lastUsedRack;

      const initialStatusMap = {};
      for (const p of pieces) {
        if (p.location) {
          const parsed = parseRackLocation(p.location);
          if (parsed.line) latestLine = parsed.line;
          if (parsed.rack) latestRack = parsed.rack;
          break;
        }
      }

      for (const p of pieces) {
        if (p.stockAuditBatchId === selectedBatchId || (p.lastStockVerifiedAt && data.existingAuditRecord)) {
          initialStatusMap[p.pieceId] = {
            saved: true,
            at: p.lastStockVerifiedAt ? new Date(p.lastStockVerifiedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Verified',
          };
        }
      }
      setPieceAuditStatusMap(initialStatusMap);
      setSelectedPieceIds(new Set());

      setLastUsedLine(latestLine);
      setLastUsedRack(latestRack);

      setPieceInputs(
        pieces.map((p) => {
          const parsed = parseRackLocation(p.location);
          const line = parsed.line || latestLine || '';
          const rack = parsed.rack || latestRack || '';
          const shelf = parsed.shelf || '';
          return {
            pieceId: p.pieceId,
            length: String(p.length || 0),
            originalLength: p.length || 0,
            rackLine: line,
            rackNumber: rack,
            shelfNumber: shelf,
            location: composeRackLocation(line, rack, shelf),
            action: 'update',
          };
        })
      );

      setNewPieceDraft({
        pieceId: '',
        length: '',
        rackLine: latestLine,
        rackNumber: latestRack,
        shelfNumber: '',
      });
    } catch (err) {
      playAudioFeedback('warning');
      triggerHaptic('warning');
      alert(`Error loading SKU: ${err.message}`);
    } finally {
      setIsLoadingSku(false);
      setScannedBarcode('');
    }
  }, [apiBase, selectedBatchId, tokenHeader, lastUsedLine, lastUsedRack]);

  const handleScanNextProduct = () => {
    setSavedSummaryData(null);
    setCurrentSkuData(null);
    setPieceInputs([]);
    setSelectedPieceIds(new Set());
    setAuditNote('');
    setReprintStatusMap({});
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  const handleRecountThisSku = () => {
    setSavedSummaryData(null);
  };

  const handleReprint = async (targetPieces = null, pieceIdKey = 'ALL') => {
    const targetSku = savedSummaryData?.sku || currentSkuData?.sku;
    if (!targetSku || !selectedBatchId) return;
    if (pieceIdKey === 'ALL') setIsReprintingAll(true);
    else setReprintingPieceId(pieceIdKey);

    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';
      const body = targetPieces ? JSON.stringify({ pieces: targetPieces }) : JSON.stringify({});

      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(targetSku)}/reprint`, {
        method: 'POST',
        headers,
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Reprint failed.');

      playAudioFeedback('success');
      triggerHaptic('success');
      setReprintStatusMap((prev) => ({ ...prev, [pieceIdKey]: 'Sent!' }));
      setTimeout(() => {
        setReprintStatusMap((prev) => ({ ...prev, [pieceIdKey]: null }));
      }, 2500);
    } catch (err) {
      alert(`Reprint error: ${err.message}`);
    } finally {
      setIsReprintingAll(false);
      setReprintingPieceId(null);
    }
  };

  // Print audited piece labels on A4 sticker sheet (ST-33 format)
  const handlePrintA4Sheet = (targetPieces = null) => {
    const piecesToPrint = targetPieces && targetPieces.length > 0
      ? targetPieces
      : (savedSummaryData?.pieces || []);

    if (!piecesToPrint || piecesToPrint.length === 0) {
      alert('No pieces to print on A4 sheet.');
      return;
    }

    const currentSkuName = currentSkuData?.sku || savedSummaryData?.sku || '';
    const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

    let startSlot = 1;
    const inputSlot = window.prompt('Enter starting sticker position on ST-33 A4 Sheet (1 - 33):', '1');
    if (inputSlot === null) return;
    const parsed = parseInt(inputSlot, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 33) {
      startSlot = parsed;
    }

    const batchName = activeBatch?.name || '';

    const jobs = piecesToPrint.map((p) => {
      const pid = String(p.pieceId || '').trim();
      const len = Number(p.length) || 0;
      const loc = p.location || '';
      const line1 = loc ? `PIECE: ${pid} • LOC: ${loc}` : `PIECE: ${pid}`;
      const line2 = currentSkuName;
      const line3 = batchName ? `${len.toFixed(2)}m • ${batchName}` : `${len.toFixed(2)}m • [${dateStr}]`;
      return {
        payload: {
          line1,
          line2,
          line3,
          barcodeText: pid,
        },
        style: {
          textCase: 'as_typed',
          textAlign: 'center',
          barcodeAlign: 'center',
        },
      };
    });

    try {
      printLaserSheet({ jobs, startSlot });
    } catch (err) {
      alert(`A4 Sheet Print Error: ${err.message || err}`);
    }
  };

  // Handle Hardware Laser Scan / Manual Enter
  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (scannedBarcode.trim()) {
      loadSkuForAudit(scannedBarcode.trim());
    }
  };

  // Toggle Camera Barcode Scanner
  const toggleCameraScanner = async () => {
    if (isCameraActive) {
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          html5QrCodeRef.current.clear();
        } catch (e) {
          console.warn('Camera stop error:', e);
        }
      }
      setIsCameraActive(false);
      setIsTorchOn(false);
    } else {
      setIsCameraActive(true);
      setTimeout(async () => {
        try {
          const qrScanner = new Html5Qrcode('mobile-audit-reader');
          html5QrCodeRef.current = qrScanner;

          await qrScanner.start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: { width: 250, height: 150 } },
            (decodedText) => {
              if (decodedText) {
                loadSkuForAudit(decodedText);
              }
            },
            () => {}
          );
        } catch (err) {
          alert(`Unable to access camera: ${err.message || err}`);
          setIsCameraActive(false);
        }
      }, 200);
    }
  };

  // Torch Toggle
  const toggleTorch = async () => {
    if (!html5QrCodeRef.current || !isCameraActive) return;
    try {
      await html5QrCodeRef.current.applyVideoConstraints({
        advanced: [{ torch: !isTorchOn }],
      });
      setIsTorchOn(!isTorchOn);
    } catch (e) {
      console.warn('Torch not supported:', e);
    }
  };

  // Handle 3-part rack change (Line, Rack, Shelf)
  const handleRackFieldChange = (idx, field, rawValue) => {
    const value = rawValue.toUpperCase().trimStart();
    const updated = [...pieceInputs];
    updated[idx][field] = value;
    if (field === 'rackLine' && value) {
      setLastUsedLine(value);
      localStorage.setItem('audit_last_line', value);
    }
    if (field === 'rackNumber' && value) {
      setLastUsedRack(value);
      localStorage.setItem('audit_last_rack', value);
    }
    updated[idx].location = composeRackLocation(
      updated[idx].rackLine,
      updated[idx].rackNumber,
      updated[idx].shelfNumber
    );
    setPieceInputs(updated);
  };

  // Step Length Helper (+1m / -1m)
  const handleStepLength = (idx, step) => {
    const updated = [...pieceInputs];
    const current = parseFloat(updated[idx].length) || 0;
    const nextVal = Math.max(0, current + step);
    updated[idx].length = String(Number(nextVal.toFixed(2)));
    if (updated[idx].action === 'disable') updated[idx].action = 'update';
    setPieceInputs(updated);
  };

  const handleMatchSystem = (idx) => {
    const updated = [...pieceInputs];
    updated[idx].length = String(updated[idx].originalLength || 0);
    updated[idx].action = 'update';
    setPieceInputs(updated);
  };

  const handleEmptyPiece = (idx) => {
    const updated = [...pieceInputs];
    if (updated[idx].action === 'disable') {
      updated[idx].action = 'update';
      updated[idx].length = String(updated[idx].originalLength || 0);
    } else {
      updated[idx].action = 'disable';
      updated[idx].length = '0';
    }
    setPieceInputs(updated);
  };

  // Add Discovered Piece
  const handleAddFoundPiece = () => {
    if (!newPieceDraft.pieceId.trim() || !newPieceDraft.length) {
      alert('Enter Piece ID and Length');
      return;
    }
    const parsed = parseFloat(newPieceDraft.length);
    if (isNaN(parsed) || parsed <= 0) return;

    const composedLoc = composeRackLocation(
      newPieceDraft.rackLine,
      newPieceDraft.rackNumber,
      newPieceDraft.shelfNumber
    );

    const newPid = newPieceDraft.pieceId.trim().toUpperCase();

    setPieceInputs([
      ...pieceInputs,
      {
        pieceId: newPid,
        length: String(parsed),
        originalLength: 0,
        rackLine: newPieceDraft.rackLine,
        rackNumber: newPieceDraft.rackNumber,
        shelfNumber: newPieceDraft.shelfNumber,
        location: composedLoc,
        action: 'add',
      },
    ]);

    setSelectedPieceIds((prev) => new Set([...prev, newPid]));

    setNewPieceDraft({
      pieceId: '',
      length: '',
      rackLine: lastUsedLine,
      rackNumber: lastUsedRack,
      shelfNumber: '',
    });
  };

  // Calculate Totals
  const currentPreStock = useMemo(() => {
    return pieceInputs.reduce((sum, p) => (p.action === 'add' ? sum : sum + (Number(p.originalLength) || 0)), 0);
  }, [pieceInputs]);

  const liveCountedStock = useMemo(() => {
    return pieceInputs.reduce((sum, p) => {
      if (p.action === 'disable') return sum;
      const val = parseFloat(p.length);
      return sum + (isNaN(val) || val < 0 ? 0 : val);
    }, 0);
  }, [pieceInputs]);

  const liveDifference = liveCountedStock - currentPreStock;
  const isVariance = Math.abs(liveDifference) > 0.05;

  // Save Count for Selected Pieces & Ready Next
  const handleSaveCount = async (ignoreConflict = false) => {
    if (!currentSkuData || !selectedBatchId) return;

    const selectedPieces = pieceInputs.filter((p) => selectedPieceIds.has(p.pieceId));
    if (selectedPieces.length === 0) {
      alert('Please select at least one piece to save & print.');
      return;
    }

    setIsSaving(true);
    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';

      const payload = {
        countStartedAt,
        pieces: selectedPieces.map((p) => ({
          pieceId: p.pieceId,
          action: p.action,
          length: parseFloat(p.length) || 0,
          location: p.location,
        })),
        note: auditNote.trim(),
        ignoreConcurrencyWarning: ignoreConflict,
      };

      const res = await fetch(`${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(currentSkuData.sku)}/count`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.status === 409 && data.concurrencyConflict) {
        setConcurrencyConflict(data);
        playAudioFeedback('warning');
        triggerHaptic('warning');
        setIsSaving(false);
        return;
      }

      if (!res.ok) throw new Error(data.message || 'Failed to save count.');

      playAudioFeedback('success');
      triggerHaptic('success');

      const activePiecesForSummary = selectedPieces
        .filter((p) => p.action !== 'disable' && parseFloat(p.length) > 0)
        .map((p) => ({
          pieceId: p.pieceId,
          length: parseFloat(p.length) || 0,
          location: p.location || '',
        }));

      // Mark selected pieces as audited
      const updatedStatus = { ...pieceAuditStatusMap };
      const currentTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      selectedPieces.forEach((p) => {
        updatedStatus[p.pieceId] = {
          saved: true,
          at: currentTimeStr,
          length: parseFloat(p.length) || 0,
          location: p.location || '',
        };
      });
      setPieceAuditStatusMap(updatedStatus);

      // Update originalLength so system baseline matches the counted pieces
      const selectedIdSet = new Set(selectedPieces.map((p) => p.pieceId));
      setPieceInputs((prev) =>
        prev.map((p) => (selectedIdSet.has(p.pieceId) ? { ...p, originalLength: parseFloat(p.length) || 0 } : p))
      );

      // Deselect saved pieces
      setSelectedPieceIds(new Set());

      setSavedSummaryData({
        sku: currentSkuData.sku,
        preStock: currentPreStock,
        countedStock: liveCountedStock,
        difference: liveDifference,
        pieces: activePiecesForSummary,
        status: Math.abs(liveDifference) < 0.05 ? 'counted' : 'discrepancy',
        note: auditNote.trim(),
      });

      fetchBatchDetails();
    } catch (err) {
      alert(`Save error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Submit count for an individual piece and immediately print 1 label
  const handleSaveSinglePiece = async (idx, ignoreConflict = false) => {
    const targetPiece = pieceInputs[idx];
    if (!targetPiece || !currentSkuData || !selectedBatchId) return;

    const pieceId = String(targetPiece.pieceId || '').trim();
    if (!pieceId) return;

    setSavingPieceId(pieceId);
    try {
      const headers = await tokenHeader();
      headers['Content-Type'] = 'application/json';

      const payload = {
        countStartedAt,
        pieces: [
          {
            pieceId: targetPiece.pieceId,
            action: targetPiece.action,
            length: parseFloat(targetPiece.length) || 0,
            location: targetPiece.location,
          },
        ],
        note: auditNote.trim() ? `${auditNote.trim()} [Piece: ${pieceId}]` : null,
        ignoreConcurrencyWarning: ignoreConflict,
      };

      const res = await fetch(
        `${apiBase}/api/v1/inventory/audit/batches/${encodeURIComponent(selectedBatchId)}/sku/${encodeURIComponent(currentSkuData.sku)}/count`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (res.status === 409 && data.concurrencyConflict) {
        setConcurrencyConflict(data);
        playAudioFeedback('warning');
        triggerHaptic('warning');
        setSavingPieceId(null);
        return;
      }

      if (!res.ok) throw new Error(data.message || 'Failed to save piece.');

      playAudioFeedback('success');
      triggerHaptic('success');

      setPieceAuditStatusMap((prev) => ({
        ...prev,
        [pieceId]: {
          saved: true,
          at: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          length: parseFloat(targetPiece.length) || 0,
          location: targetPiece.location || '',
        },
      }));

      // Deselect if it was checked
      setSelectedPieceIds((prev) => {
        const next = new Set(prev);
        next.delete(pieceId);
        return next;
      });

      setPieceInputs((prev) =>
        prev.map((p, i) => (i === idx ? { ...p, originalLength: parseFloat(p.length) || 0 } : p))
      );

      fetchBatchDetails();
    } catch (err) {
      alert(`Save error for piece ${pieceId}: ${err.message}`);
    } finally {
      setSavingPieceId(null);
    }
  };

  // Filtered SKUs for current rack
  const filteredRackSkus = useMemo(() => {
    if (activeRack === 'ALL') return rackSkus;
    return rackSkus.filter((s) => s.locations?.some((l) => l.toUpperCase().includes(activeRack)));
  }, [rackSkus, activeRack]);

  const rackCountedCount = filteredRackSkus.filter((s) => s.isCounted).length;
  const rackPercent = filteredRackSkus.length > 0 ? Math.round((rackCountedCount / filteredRackSkus.length) * 100) : 0;

  return (
    <div className="mobile-audit-container">
      {/* Header */}
      <div className="mobile-audit-header">
        <div className="mobile-audit-header-top">
          <div className="mobile-audit-title" onClick={() => navigate('/inventory')}>
            <span>←</span> Stock Audit Scanner
          </div>
          <button
            className="mobile-audit-rack-chip"
            onClick={() => setShowRackListModal(true)}
            style={{ background: '#3b82f6', color: '#fff', border: 'none' }}
          >
            📋 Rack List ({filteredRackSkus.length})
          </button>
        </div>

        {/* Batch Dropdown */}
        {batches.length > 0 && (
          <select
            className="mobile-audit-batch-select"
            value={selectedBatchId}
            onChange={(e) => setSelectedBatchId(e.target.value)}
          >
            {batches.map((b) => (
              <option key={b.batchId || b.id} value={b.batchId || b.id}>
                {b.name} ({b.status === 'completed' ? 'Done' : 'Active'})
              </option>
            ))}
          </select>
        )}
        {activeBatch && (
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '6px' }}>
            Batch: {activeBatch.targetSkusCount || 0} Target | {activeBatch.discrepancyCount || 0} Variances
          </div>
        )}

        {/* Rack Filters */}
        <div className="mobile-audit-racks-bar">
          {availableRacks.map((rack) => (
            <button
              key={rack}
              className={`mobile-audit-rack-chip ${activeRack === rack ? 'active' : ''}`}
              onClick={() => setActiveRack(rack)}
            >
              {rack === 'ALL' ? 'All Racks' : `📍 ${rack}`}
            </button>
          ))}
        </div>

        {/* Progress Tracker */}
        <div className="mobile-audit-progress-row">
          <span>
            {activeRack}: {rackCountedCount}/{filteredRackSkus.length} Counted
          </span>
          <span>{rackPercent}%</span>
        </div>
        <div className="mobile-audit-progress-track">
          <div className="mobile-audit-progress-fill" style={{ width: `${rackPercent}%` }}></div>
        </div>
      </div>

      {/* Barcode Scanner Card */}
      <div className="mobile-audit-scan-card">
        <form onSubmit={handleBarcodeSubmit} className="mobile-audit-input-group">
          <input
            ref={barcodeInputRef}
            type="text"
            className="mobile-audit-barcode-input"
            placeholder="Scan / Type SKU Barcode..."
            value={scannedBarcode}
            onChange={(e) => setScannedBarcode(e.target.value)}
          />
          <button
            type="button"
            className={`mobile-audit-camera-btn ${isCameraActive ? 'active' : ''}`}
            onClick={toggleCameraScanner}
            title="Toggle Camera Viewfinder"
          >
            {isCameraActive ? '✕' : '📷'}
          </button>
        </form>

        {isCameraActive && (
          <div className="mobile-audit-camera-box">
            <div id="mobile-audit-reader"></div>
            <div className="mobile-audit-camera-overlay">
              <button type="button" className="mobile-audit-torch-btn" onClick={toggleTorch}>
                {isTorchOn ? '🔦 Torch On' : '🔦 Torch Off'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading state */}
      {isLoadingSku && (
        <div style={{ padding: '30px', textAlign: 'center', fontWeight: '600' }}>
          Loading physical piece data...
        </div>
      )}

      {/* Summary Screen (After Save & Print) */}
      {savedSummaryData && !isLoadingSku && (
        <div className="mobile-audit-sku-card mobile-summary-card">
          <div className="mobile-summary-banner">
            <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>✅</span>
            <div>
              <div style={{ fontWeight: '800', fontSize: '1.05rem', color: '#065f46' }}>
                Count Saved & Labels Printed!
              </div>
              <div style={{ fontSize: '0.8rem', color: '#047857', marginTop: '2px' }}>
                {savedSummaryData.pieces.length} sticker(s) queued for {savedSummaryData.sku}
              </div>
            </div>
          </div>

          {/* Variance Card */}
          <div className={`mobile-audit-variance-card ${savedSummaryData.status === 'counted' ? 'match' : 'discrepancy'}`}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Counted Total</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#0f172a' }}>
                {savedSummaryData.countedStock.toFixed(2)}m
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              {Math.abs(savedSummaryData.difference) < 0.05 ? (
                <span className="mobile-audit-status-pill match">✓ EXACT MATCH</span>
              ) : savedSummaryData.difference > 0 ? (
                <span className="mobile-audit-status-pill over">+{savedSummaryData.difference.toFixed(2)}m OVER</span>
              ) : (
                <span className="mobile-audit-status-pill short">{savedSummaryData.difference.toFixed(2)}m SHORT</span>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Difference</div>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontWeight: '800',
                  color: savedSummaryData.difference > 0 ? '#16a34a' : savedSummaryData.difference < 0 ? '#dc2626' : '#64748b',
                }}
              >
                {savedSummaryData.difference > 0 ? `+${savedSummaryData.difference.toFixed(2)}m` : `${savedSummaryData.difference.toFixed(2)}m`}
              </div>
            </div>
          </div>

          {/* Printed Pieces List with Reprint */}
          <div className="mobile-summary-pieces-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#1e293b' }}>
                Printed Pieces ({savedSummaryData.pieces.length} pcs)
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  className="mobile-summary-a4-btn"
                  disabled={savedSummaryData.pieces.length === 0}
                  onClick={() => handlePrintA4Sheet(savedSummaryData.pieces)}
                  title="Print on A4 sticker sheet (ST-33)"
                >
                  📄 A4 Sheet
                </button>
                <button
                  type="button"
                  className="mobile-summary-reprint-btn all"
                  disabled={isReprintingAll || savedSummaryData.pieces.length === 0}
                  onClick={() => handleReprint(savedSummaryData.pieces, 'ALL')}
                  title="Send to thermal label printer"
                >
                  {isReprintingAll ? '⏳...' : reprintStatusMap['ALL'] ? `✓` : '🖨️ Thermal'}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {savedSummaryData.pieces.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem', background: '#f8fafc', borderRadius: '6px' }}>
                  No active pieces remaining.
                </div>
              ) : (
                savedSummaryData.pieces.map((p) => {
                  const isPieceReprinting = reprintingPieceId === p.pieceId;
                  const pieceReprintStatus = reprintStatusMap[p.pieceId];
                  return (
                    <div key={p.pieceId} className="mobile-summary-piece-row">
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '0.9rem', color: '#0f172a' }}>🏷️ Piece #{p.pieceId}</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                          <span style={{ color: '#059669', fontWeight: '700' }}>{p.length}m</span> • Loc: {p.location || 'No rack'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          className="mobile-piece-a4-btn"
                          onClick={() => handlePrintA4Sheet([p])}
                          title="Print A4"
                        >
                          📄 A4
                        </button>
                        <button
                          type="button"
                          className={`mobile-summary-reprint-btn single ${pieceReprintStatus ? 'sent' : ''}`}
                          disabled={isPieceReprinting}
                          onClick={() => handleReprint([p], p.pieceId)}
                          title="Reprint thermal label"
                        >
                          {isPieceReprinting ? '⏳...' : pieceReprintStatus ? `✓ ${pieceReprintStatus}` : '🖨️ Thermal'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mobile-summary-actions">
            <button type="button" className="mobile-summary-btn-secondary" onClick={handleRecountThisSku}>
              ✏️ Edit / Recount
            </button>
            <button type="button" className="mobile-summary-btn-primary" onClick={handleScanNextProduct}>
              ➡️ Next Product (Scan Next)
            </button>
          </div>
        </div>
      )}

      {/* Active Scanned SKU Card (Counting Mode) */}
      {currentSkuData && !isLoadingSku && !savedSummaryData && (
        <div className="mobile-audit-sku-card">
          <div className="mobile-audit-sku-header">
            <div>
              <div className="mobile-audit-sku-name">{currentSkuData.sku}</div>
              <div className="mobile-audit-sku-locations">
                Rack: {currentSkuData.pieces?.map((p) => p.location).filter(Boolean).join(', ') || 'No rack'}
              </div>
            </div>
            <div className="mobile-audit-system-badge">Sys: {currentPreStock.toFixed(2)}m</div>
          </div>

          {/* Concurrency conflict banner */}
          {concurrencyConflict && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
              <div style={{ fontWeight: '700', color: '#b45309', fontSize: '0.85rem' }}>
                ⚠️ Concurrent Order Detected While Counting!
              </div>
              <p style={{ margin: '4px 0', fontSize: '0.8rem', color: '#78350f' }}>
                An order was deducted in system. Did your count exclude it?
              </p>
              <button
                type="button"
                style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', marginTop: '4px' }}
                onClick={() => handleSaveCount(true)}
              >
                Yes, Save My Count
              </button>
            </div>
          )}

          {/* Piece verification list */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '12px 0 8px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>
              Active Pieces ({pieceInputs.length})
            </div>
            {pieceInputs.length > 0 && activeBatch?.status !== 'completed' && (
              <button
                type="button"
                className="mobile-select-toggle-btn"
                onClick={() => {
                  const selectableIds = pieceInputs.filter((p) => p.action !== 'disable').map((p) => p.pieceId);
                  if (selectedPieceIds.size === selectableIds.length && selectableIds.length > 0) {
                    setSelectedPieceIds(new Set());
                  } else {
                    setSelectedPieceIds(new Set(selectableIds));
                  }
                }}
              >
                {selectedPieceIds.size === pieceInputs.filter((p) => p.action !== 'disable').length && pieceInputs.length > 0
                  ? 'Deselect All'
                  : `Select All (${pieceInputs.length})`}
              </button>
            )}
          </div>

          {pieceInputs.map((p, idx) => {
            const isPieceSaved = !!pieceAuditStatusMap[p.pieceId]?.saved;
            const isThisPieceSaving = savingPieceId === p.pieceId;
            const isThisPieceReprinting = reprintingPieceId === p.pieceId;
            const pieceReprintStatus = reprintStatusMap[p.pieceId];
            const isSelected = selectedPieceIds.has(p.pieceId);

            return (
              <div
                key={p.pieceId}
                className={`mobile-piece-item ${p.action === 'disable' ? 'disabled' : ''} ${isPieceSaved ? 'audited' : ''} ${isSelected ? 'selected' : ''}`}
              >
                <div className="mobile-piece-top">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label className="mobile-piece-checkbox-label">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={p.action === 'disable' || activeBatch?.status === 'completed'}
                        onChange={(e) => {
                          const next = new Set(selectedPieceIds);
                          if (e.target.checked) next.add(p.pieceId);
                          else next.delete(p.pieceId);
                          setSelectedPieceIds(next);
                        }}
                        className="mobile-piece-checkbox"
                      />
                      <span className="mobile-piece-id">
                        Piece ID: {p.pieceId} {p.action === 'add' ? <span className="mobile-piece-new-badge">[NEW]</span> : ''}
                      </span>
                    </label>
                    {isPieceSaved && (
                      <span className="mobile-piece-saved-pill" title={`Audited at ${pieceAuditStatusMap[p.pieceId]?.at || ''}`}>
                        ✓ Audited
                      </span>
                    )}
                  </div>
                  <span className="mobile-piece-sys-badge">Sys: {p.originalLength}m</span>
                </div>

                {/* 3-Tier Rack Location: Line - Rack - Shelf */}
                <div className="mobile-rack-triplet">
                  <span className="mobile-rack-label-prefix">📍 Location:</span>
                  <div className="mobile-rack-field">
                    <label>Line</label>
                    <input
                      type="text"
                      placeholder="e.g. A"
                      value={p.rackLine || ''}
                      disabled={p.action === 'disable'}
                      onChange={(e) => handleRackFieldChange(idx, 'rackLine', e.target.value)}
                      className="mobile-rack-input"
                    />
                  </div>
                  <span className="mobile-rack-sep">-</span>
                  <div className="mobile-rack-field">
                    <label>Rack</label>
                    <input
                      type="text"
                      placeholder="e.g. 03"
                      value={p.rackNumber || ''}
                      disabled={p.action === 'disable'}
                      onChange={(e) => handleRackFieldChange(idx, 'rackNumber', e.target.value)}
                      className="mobile-rack-input"
                    />
                  </div>
                  <span className="mobile-rack-sep">-</span>
                  <div className="mobile-rack-field">
                    <label>Shelf</label>
                    <input
                      type="text"
                      placeholder="e.g. 2"
                      value={p.shelfNumber || ''}
                      disabled={p.action === 'disable'}
                      onChange={(e) => handleRackFieldChange(idx, 'shelfNumber', e.target.value)}
                      className="mobile-rack-input"
                    />
                  </div>
                  {p.location && (
                    <div className="mobile-rack-composed-badge" title="Combined location">
                      📍 {p.location}
                    </div>
                  )}
                </div>

                <div className="mobile-piece-controls">
                  <button type="button" className="mobile-piece-btn-step btn-minus" disabled={p.action === 'disable'} onClick={() => handleStepLength(idx, -1)} title="Minus 1 meter">
                    -1
                  </button>
                  <button type="button" className="mobile-piece-btn-step btn-minus step-half" disabled={p.action === 'disable'} onClick={() => handleStepLength(idx, -0.5)} title="Minus 0.5 meter">
                    -0.5
                  </button>
                  <div className="mobile-piece-input-wrap">
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      className="mobile-piece-input-length"
                      value={p.length}
                      disabled={p.action === 'disable'}
                      onChange={(e) => {
                        const updated = [...pieceInputs];
                        updated[idx].length = e.target.value;
                        if (updated[idx].action === 'disable') updated[idx].action = 'update';
                        setPieceInputs(updated);
                      }}
                    />
                    <span className="mobile-piece-unit">m</span>
                  </div>
                  <button type="button" className="mobile-piece-btn-step btn-plus step-half" disabled={p.action === 'disable'} onClick={() => handleStepLength(idx, 0.5)} title="Plus 0.5 meter">
                    +0.5
                  </button>
                  <button type="button" className="mobile-piece-btn-step btn-plus" disabled={p.action === 'disable'} onClick={() => handleStepLength(idx, 1)} title="Plus 1 meter">
                    +1
                  </button>
                </div>

                {/* Unified Single-Line Action Bar: Match | Empty | Save & Print | Reprint */}
                <div className="mobile-piece-unified-actions">
                  <button
                    type="button"
                    className="mobile-piece-quick-btn match"
                    disabled={p.action === 'disable'}
                    onClick={() => handleMatchSystem(idx)}
                    title={`Match system (${p.originalLength}m)`}
                  >
                    ✓ Match ({p.originalLength}m)
                  </button>
                  <button
                    type="button"
                    className={`mobile-piece-quick-btn empty ${p.action === 'disable' ? 'is-disabled' : ''}`}
                    onClick={() => handleEmptyPiece(idx)}
                    title={p.action === 'disable' ? 'Restore piece' : 'Mark as empty/lost'}
                  >
                    {p.action === 'disable' ? '↩ Undo' : '✕ Empty / Lost'}
                  </button>
                  <button
                    type="button"
                    className={`mobile-piece-save-btn ${isPieceSaved ? 'saved' : ''}`}
                    disabled={isThisPieceSaving || isSaving || activeBatch?.status === 'completed'}
                    onClick={() => handleSaveSinglePiece(idx)}
                    title={activeBatch?.status === 'completed' ? 'Batch is completed (Read-only)' : 'Save this piece & immediately print 1 label'}
                  >
                    {isThisPieceSaving
                      ? '⏳ Saving...'
                      : isPieceSaved
                      ? '✓ Saved (Again)'
                      : '💾 Save & Print'}
                  </button>
                  {isPieceSaved && (
                    <>
                      <button
                        type="button"
                        className="mobile-piece-a4-btn"
                        onClick={() => handlePrintA4Sheet([p])}
                        title="Print A4 sticker"
                      >
                        📄 A4
                      </button>
                      <button
                        type="button"
                        className="mobile-piece-reprint-btn"
                        disabled={isThisPieceReprinting}
                        onClick={() => handleReprint([p], p.pieceId)}
                        title="Reprint thermal label"
                      >
                        {isThisPieceReprinting ? '⏳' : pieceReprintStatus ? `✓` : '🖨️'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add Found Piece 2-Row Card */}
          <div className="mobile-found-piece-card">
            <div className="mobile-found-piece-title">+ Found Unrecorded Piece on Shelf?</div>
            <div className="mobile-found-piece-row1">
              <input
                type="text"
                placeholder="Piece ID (e.g. 104)"
                value={newPieceDraft.pieceId}
                onChange={(e) => setNewPieceDraft({ ...newPieceDraft, pieceId: e.target.value })}
                className="mobile-found-input flex-1"
              />
              <div className="mobile-found-len-wrap">
                <input
                  type="number"
                  step="0.25"
                  placeholder="Meters (e.g. 5.5)"
                  value={newPieceDraft.length}
                  onChange={(e) => setNewPieceDraft({ ...newPieceDraft, length: e.target.value })}
                  className="mobile-found-input"
                />
                <span className="mobile-found-unit">m</span>
              </div>
            </div>
            <div className="mobile-found-piece-row2">
              <div className="mobile-found-loc-group">
                <input
                  type="text"
                  placeholder="Line"
                  value={newPieceDraft.rackLine}
                  onChange={(e) => setNewPieceDraft({ ...newPieceDraft, rackLine: e.target.value.toUpperCase() })}
                  className="mobile-found-loc-input"
                />
                <span className="mobile-rack-sep" style={{ marginTop: 0 }}>-</span>
                <input
                  type="text"
                  placeholder="Rack"
                  value={newPieceDraft.rackNumber}
                  onChange={(e) => setNewPieceDraft({ ...newPieceDraft, rackNumber: e.target.value.toUpperCase() })}
                  className="mobile-found-loc-input"
                />
                <span className="mobile-rack-sep" style={{ marginTop: 0 }}>-</span>
                <input
                  type="text"
                  placeholder="Shelf"
                  value={newPieceDraft.shelfNumber}
                  onChange={(e) => setNewPieceDraft({ ...newPieceDraft, shelfNumber: e.target.value.toUpperCase() })}
                  className="mobile-found-loc-input"
                />
              </div>
              <button
                type="button"
                className="mobile-found-add-btn"
                onClick={handleAddFoundPiece}
              >
                + Add Piece
              </button>
            </div>
          </div>

          {/* Variance Card with Status Pill */}
          <div className={`mobile-audit-variance-card ${isVariance ? 'discrepancy' : 'match'}`}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Counted Available</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#0f172a' }}>{liveCountedStock.toFixed(2)}m</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              {!isVariance ? (
                <span className="mobile-audit-status-pill match">✓ EXACT MATCH</span>
              ) : liveDifference > 0 ? (
                <span className="mobile-audit-status-pill over">+{liveDifference.toFixed(2)}m OVER</span>
              ) : (
                <span className="mobile-audit-status-pill short">{liveDifference.toFixed(2)}m SHORT</span>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Difference</div>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontWeight: '800',
                  color: liveDifference > 0 ? '#16a34a' : liveDifference < 0 ? '#dc2626' : '#64748b',
                }}
              >
                {liveDifference > 0 ? `+${liveDifference.toFixed(2)}m` : `${liveDifference.toFixed(2)}m`}
              </div>
            </div>
          </div>

          {/* Quick Note Reason Chips */}
          <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Quick Audit Note:</div>
          <div className="mobile-audit-note-chips">
            {QUICK_NOTE_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                className={`mobile-audit-note-chip ${auditNote.includes(chip) ? 'selected' : ''}`}
                onClick={() => setAuditNote(chip)}
              >
                {chip}
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Custom note (e.g., cutting waste, recount confirmed)..."
            value={auditNote}
            onChange={(e) => setAuditNote(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
          />
        </div>
      )}

      {/* Floating Bottom Action Bar */}
      {currentSkuData && !savedSummaryData && (
        <div className="mobile-audit-bottom-bar">
          {activeBatch?.status === 'completed' ? (
            <div style={{ textAlign: 'center', width: '100%', padding: '12px', background: '#f1f5f9', color: '#64748b', borderRadius: '8px', fontWeight: '700', fontSize: '0.9rem', border: '1px solid #cbd5e1' }}>
              🔒 Audit Batch Completed (Read-Only)
            </div>
          ) : (
            <button
              type="button"
              className={`mobile-audit-save-btn ${selectedPieceIds.size === 0 ? 'btn-disabled-muted' : ''}`}
              disabled={isSaving || selectedPieceIds.size === 0}
              onClick={() => handleSaveCount(false)}
            >
              {isSaving
                ? 'Saving & Printing...'
                : selectedPieceIds.size === 0
                ? 'SELECT PIECES TO SAVE'
                : `💾 SAVE & PRINT SELECTED (${selectedPieceIds.size})`}
            </button>
          )}
        </div>
      )}

      {/* Rack SKUs List Modal */}
      {showRackListModal && (
        <Modal isOpen={true} onClose={() => setShowRackListModal(false)}>
          <div style={{ padding: '16px', maxHeight: '75vh', overflowY: 'auto' }}>
            <h3>SKUs on {activeRack === 'ALL' ? 'All Racks' : activeRack}</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Tap any SKU to verify without scanning barcode:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredRackSkus.map((item) => (
                <div
                  key={item.sku}
                  onClick={() => {
                    setShowRackListModal(false);
                    loadSkuForAudit(item.sku);
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: item.isCounted ? '#f0fdf4' : '#ffffff',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <strong>{item.sku}</strong>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {item.locations?.join(', ') || 'No rack'} | {item.pieceCount} pcs ({item.currentStock}m)
                    </div>
                  </div>
                  <div>
                    {item.isCounted ? (
                      <span style={{ color: '#16a34a', fontWeight: '700', fontSize: '0.85rem' }}>✓ Counted</span>
                    ) : (
                      <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '0.85rem' }}>Pending →</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default StockAuditMobilePage;
